<template>

    <div class="equipment-view-compliance-actions"
         v-if="showComponent">
        <b-card no-body>
            <b-card-header class="pod-heading">
                <h5>Select Compliance Plan</h5>
                <div class="heading__buttons">
                    <compliance-plan-badge :equipment="equipment"/>
                </div>
            </b-card-header>
            <b-card-body>

                <template v-if="complianceStatus === 'COMPLIANT'">
                    Equipment is compliant - No compliance plan required.
                </template>

                <template v-else-if="complianceStatus === 'NONCOMPLIANT'">
                    <p>
                        Vehicles and equipment that do not meet the current Tier requirements may be repowered or
                        retrofit in order to come into compliance. Under some limited cases, exemptions may be allowed
                        after following the Compliance Options Protocols.
                    </p>
                    <p>
                        The Compliance Options Protocols guide equipment owners through which compliance option
                        applies to their equipment following a "Best Available Technology" approach. In other words, the
                        compliance option for a specific piece of equipment must be the option that reduces the diesel
                        particulate matter the most within the capabilities of that piece of equipment. Compliance with
                        the CAC Diesel Engine Requirements will be determined according to the following protocols.
                        Visit <a target="_blank" :href="infoLink">{{ infoLink }}</a>
                        or read below for details.
                    </p>

                    <b-card no-body class="mt-3">
                        <b-card-header>
                            <h5>Option {{ shownAction + 1 }}: {{ currentAction.title }}</h5>
                        </b-card-header>
                        <b-card-body>
                            <intro :route="introKey" :key="introKey"/>
                            <br/>
                            <b-form-radio v-model="selectedAction" :value="shownAction"
                                          @change="actionSelected()">
                                Select this option
                            </b-form-radio>

                            <!-- nav buttons to select option - hide if an exemption has been created -->
                            <div class="position-relative compliance-plan-navigation mt-3">
                                <b-btn variant="secondary" size="sm" class="float-lg-left mb-3"
                                       v-if="shownAction > 0"
                                       @click="previousAction()">
                                    <b-icon-arrow-left-square/>
                                    Show Previous Option
                                </b-btn>
                                <b-btn variant="secondary" size="sm" class="float-lg-right"
                                       v-if="shownAction < actions.length - 1"
                                       @click="nextAction()">
                                    Show Next Option
                                    <b-icon-arrow-right-square/>
                                </b-btn>
                                <b-btn variant="danger" size="sm" class="button-clear-exemption"
                                       v-if="selectedActionValue"
                                       @click="clearAction()">
                                    Clear Option Selection
                                </b-btn>
                            </div>

                            <div v-if="canEdit && currentAction.value === 'EXEMPTION'">
                                <equipment-view-compliance-exemptions :equipment="equipment"
                                                                      :can-create="selectedActionValue === 'EXEMPTION'"
                                                                      @updated="emitUpdated"/>
                            </div>
                        </b-card-body>
                    </b-card>
                </template>

            </b-card-body>
        </b-card>
    </div>

</template>

<script>
import Vue from 'vue'
import * as _ from 'lodash'
import Authorization from '@/providers/Authorization'

export default Vue.extend({
    name: 'EquipmentViewComplianceActions',
    props: {
        equipment: Object,
    },
    data () {
        const actions = [
            { value: 'REPOWER', title: 'Repower' },
            { value: 'RETROFIT', title: 'Retrofit' },
            { value: 'RENTAL', title: 'Rent Equipment' },
            { value: 'EXEMPTION', title: 'Request Exemption' },
        ]
        const savedAction = _.find(actions, (a) => {
            return a.value === this.equipment.compliance_plan // compliance_plan is e.g. 'REPOWER'
        })
        const selectedAction = savedAction ? actions.indexOf(savedAction) : null // int

        return {
            infoLink: 'https://www.portland.gov/omf/brfs/procurement/clean-air-construction/overview-and-requirements',
            shownAction: selectedAction || 0,
            actions: actions,
            selectedAction: selectedAction,
            canEdit: false,
        }
    },
    computed: {
        isAttested () {
            return this.equipment.attestationStatus === 'ATTESTED'
        },
        exemptions () {
            return this.equipment.exemptions
        },
        complianceStatus () {
            const compliance = _.last(this.equipment.compliances)
            if (compliance) {
                return compliance.status
            } else {
                return null
            }
        },
        currentAction () {
            return this.actions[this.shownAction]
        },
        selectedActionValue () {
            if (this.selectedAction !== null) {
                return this.actions[this.selectedAction].value
            } else {
                return null
            }
        },
        selectedActionTitle () {
            if (this.selectedAction !== null) {
                return this.actions[this.selectedAction].title
            } else {
                return 'None'
            }
        },
        introKey () {
            return `complianceActions_${this.currentAction.value}`
        },
        showComponent () {
            return this.complianceStatus === 'COMPLIANT' || this.complianceStatus === 'NONCOMPLIANT'
        }
    },
    methods: {
        nextAction () {
            this.shownAction += 1
        },
        previousAction () {
            this.shownAction -= 1
        },
        async clearAction () {
            if (this.hasOutstandingExemptions()) {
                this.$bvModal.msgBoxOk('In order to clear this option selection, you must delete any Exemption Requests that have not been granted or denied.')
            } else {
                this.selectedAction = null
                await this.$api.updateEquipmentCompliancePlan(this, this.equipment.id, null)
                this.emitUpdated()
            }
        },
        emitUpdated () {
            this.$emit('updated')
        },
        async actionSelected () {
            const value = this.selectedActionValue // e.g. 'REPOWER'
            await this.$api.updateEquipmentCompliancePlan(this, this.equipment.id, value)
            this.$emit('updated')
        },
        hasOutstandingExemptions () {
            return _.some(this.equipment.exemptions, (exemption) => {
                return this.isOutstandingExemption(exemption)
            })
        },
        isOutstandingExemption (exemption) {
            return exemption.status === 'DRAFT' || exemption.status === 'PENDING' // PENDING = submitted
        },
    },
    created () {
        Authorization.can({ action: 'edit', model: 'exemption' }).then((can) => {
            this.canEdit = can
        })
    }
})
</script>

<style lang="scss">
    .button-clear-exemption {
        border-radius: .2rem !important;
        position: absolute;
        right: 0;
        top: -50px;

    }

    .compliance-plan-navigation {
        z-index: 1;
        border-top: solid 1px #ccc;
        padding-top: 1em;


        @media (max-width: 991px) {
            .btn-secondary {
                width: 100%;
            }
        }
    }

    @media (max-width: 500px) {
        .compliance-plan-navigation {
            margin-top: 60px !important;
        }
        .button-clear-exemption {
            left: 0;
            right: auto;
        }

    }

</style>